import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { iif, of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { FileUploadsService } from '@app/services/file-uploads.service';
import { PortfolioService } from '@app/services/portfolio.service';
import { PropertiesService } from '@app/services/properties.service';
import { RestapiService } from '@app/services/restapi.service';
import { UnitsService } from '@app/services/units.service';
import {
  loadAllUserProperties,
  loadAllUserPropertiesFailed,
  loadAllUserPropertiesSucceeded,
} from '@main-application/store/actions/user.actions';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { UnitAttachmentType } from '@shared/enums/attachment-type';
import {
  CreatePropertyCollectionReqI,
  RestPortfolioModel,
  RestPropertyCollectionModel,
  RestPropertyModel,
  UpdatePropertyCollectionReqI,
} from '@shared/interfaces/rest-portfolio-model.interface';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  addPropertiesToCollection,
  addPropertiesToCollectionError,
  addPropertiesToCollectionSuccess,
  addPropertyCollection,
  addPropertyCollectionError,
  addPropertyCollectionSuccess,
  addPropertyToPortfolio,
  addUsersToCollection,
  addUsersToCollectionError,
  addUsersToCollectionSuccess,
  changePropertyManagers,
  createNewPortfolio,
  deleteFileFromRepository,
  deleteFileFromRepositoryFailed,
  deleteFileFromRepositorySuccess,
  deletePortfolio,
  deletePropertyCollection,
  deletePropertyCollectionError,
  deletePropertyCollectionSuccess,
  deleteUnitAttachment,
  deleteUnitAttachmentFailed,
  deleteUnitAttachmentSuccess,
  getPropertyUnits,
  getPropertyUnitsFailed,
  getPropertyUnitsSuccess,
  getUnitAttachments,
  getUnitAttachmentsSuccess,
  loadAllUserCollections,
  loadAllUserCollectionsFailed,
  loadAllUserCollectionsSuccess,
  loadAllUserPortfolios,
  loadAllUserPortfoliosFailed,
  loadAllUserPortfoliosSuccess,
  loadAllUserUnassignedProperties,
  loadAllUserUnassignedPropertiesFailed,
  loadAllUserUnassignedPropertiesSucceeded,
  loadPortfolioDetails,
  loadPortfolioDetailsFailed,
  loadPortfolioDetailsSuccess,
  loadPropertyCollections,
  loadPropertyCollectionsFailed,
  loadPropertyCollectionsSuccess,
  loadPropertyDetails,
  loadPropertyDetailsFailed,
  loadPropertyDetailsSuccess,
  loadPropertyManagers,
  loadPropertyManagersFailed,
  loadPropertyManagersForProperty,
  loadPropertyManagersForPropertyFailed,
  loadPropertyManagersForPropertySuccess,
  loadPropertyManagersSucceeded,
  removePropertiesFromCollection,
  removePropertiesFromCollectionError,
  removePropertiesFromCollectionSuccess,
  removePropertyFromPortfolio,
  removePropertyManagers,
  removePropertyManagersFailed,
  removePropertyManagersSuccess,
  removeUsersFromCollection,
  removeUsersFromCollectionError,
  removeUsersFromCollectionSuccess,
  savePropertyCollection,
  savePropertyCollectionError,
  savePropertyCollectionSuccess,
  setPropertyManagers,
  setPropertyManagersFailed,
  setPropertyManagersSuccess,
  setUnitAttachment,
  setUnitAttachmentFail,
  setUnitAttachmentSuccess,
  updatePortfolioAutoAdd,
  updatePortfolioDetails,
  updatePortfolioDetailsFailed,
  updatePortfolioDetailsSuccess,
  updatePortfolioName,
  updateProperty,
  updatePropertyFailed,
} from './portfolio.actions';
import { selectPortfolioDetails, selectPropertyDetails } from './portfolio.selectors';

@Injectable()
export class PortfolioEffects {
  loadAllPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllUserPortfolios),
      exhaustMap(() =>
        this.restApiService.getData<RestPortfolioModel[]>(`portfolio`).pipe(
          map(result => loadAllUserPortfoliosSuccess({ portfolioItems: result || [] })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingPortfoliosList);
            return of(loadAllUserPortfoliosFailed());
          })
        )
      )
    )
  );

  loadPortfolioDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPortfolioDetails),
      switchMap(action =>
        this.restApiService.getData<RestPortfolioModel>(`portfolio/${action.portfolioId}`).pipe(
          map(result => loadPortfolioDetailsSuccess({ portfolioDetails: result })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingPortfolio);
            return of(loadPortfolioDetailsFailed());
          })
        )
      )
    )
  );

  loadPropertyDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPropertyDetails),
      switchMap(action =>
        this.restApiService.getData<RestPropertyModel>(`properties/${action.propertyId}`).pipe(
          map(result => loadPropertyDetailsSuccess({ propertyDetails: result || [] })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingPropertyDetails);
            return of(loadPropertyDetailsFailed());
          })
        )
      )
    )
  );

  loadAllUserProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllUserProperties),
      switchMap(() =>
        this.propertiesService.getAllUserProperties().pipe(
          map(properties => loadAllUserPropertiesSucceeded({ properties })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingAllUserProperties);
            return of(loadAllUserPropertiesFailed());
          })
        )
      )
    )
  );

  loadAllUserUnassignedProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllUserUnassignedProperties),
      switchMap(() =>
        this.propertiesService.getAllUserUnassignedProperties().pipe(
          map(unassignedProperties => loadAllUserUnassignedPropertiesSucceeded({ unassignedProperties })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingUnassignedUserProperties);
            return of(loadAllUserUnassignedPropertiesFailed());
          })
        )
      )
    )
  );

  updatePortfolioName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePortfolioName),
      mergeMap(action =>
        this.portfolioService.update(action.portfolioDetails).pipe(
          withLatestFrom(this.store.select(selectPortfolioDetails)),
          mergeMap(([updatedPortfolioDetails, portfolioDetails]) => [
            loadPortfolioDetailsSuccess({
              portfolioDetails: {
                ...portfolioDetails,
                name: updatedPortfolioDetails.name,
              },
            }),
            loadAllUserPortfolios(),
          ]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.UpadtingPortfolio);
            return of(updatePortfolioDetailsFailed());
          })
        )
      )
    )
  );

  updatePortfolioDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePortfolioDetails),
      mergeMap(action =>
        this.portfolioService.update(action.portfolioDetails).pipe(
          mergeMap(portfolioDetails =>
            iif(
              () => action.doNotRefetchPortfolios,
              [updatePortfolioDetailsSuccess({ portfolioDetails })],
              [updatePortfolioDetailsSuccess({ portfolioDetails }), loadAllUserPortfolios()]
            )
          ),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.UpadtingPortfolio);
            return of(updatePortfolioDetailsFailed());
          })
        )
      )
    )
  );

  updatePortfolioAutoAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePortfolioAutoAdd),
      mergeMap(action =>
        this.restApiService
          .update<RestPortfolioModel>(`portfolio/${action.portfolioDetails.id}`, action.portfolioDetails)
          .pipe(
            withLatestFrom(this.store.select(selectPortfolioDetails)),
            mergeMap(([updatedPortfolioDetails, portfolioDetails]) => [
              loadPortfolioDetailsSuccess({
                portfolioDetails: {
                  ...portfolioDetails,
                  autoAddNewProperties: updatedPortfolioDetails.autoAddNewProperties,
                  preleaseFilterMode: updatedPortfolioDetails.preleaseFilterMode,
                },
              }),
            ]),
            catchError(err => {
              this.snackbarService.error(SnackbarErrorMessage.UpadtingPortfolio);
              return of(updatePortfolioDetailsFailed());
            })
          )
      )
    )
  );

  addPropertyToPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addPropertyToPortfolio),
      mergeMap(action =>
        this.restApiService
          .update<RestPortfolioModel>(`Portfolio/${action.portfolioId}/AddProperties`, action.propertyIds)
          .pipe(
            mergeMap(portfolioDetails => {
              return [
                loadPortfolioDetails({
                  portfolioId: action.portfolioId,
                }),
                loadAllUserUnassignedProperties(),
                loadAllUserProperties(),
              ];
            }),
            catchError(err => {
              this.snackbarService.error(SnackbarErrorMessage.WhileAddingNewPropertyToPortfolio);
              return of(updatePortfolioDetailsFailed());
            })
          )
      )
    )
  );

  removePropertyFromPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removePropertyFromPortfolio),
      mergeMap(action =>
        this.restApiService
          .update<RestPortfolioModel>(`Portfolio/${action.portfolioId}/RemoveProperties`, [action.propertyId])
          .pipe(
            mergeMap(() => {
              return [
                loadPortfolioDetails({
                  portfolioId: action.portfolioId,
                }),
                loadAllUserUnassignedProperties(),
                loadAllUserProperties(),
              ];
            }),
            catchError(err => {
              this.snackbarService.error(SnackbarErrorMessage.WhileDeletingPropertyFromPortfolio);
              return of(updatePortfolioDetailsFailed());
            })
          )
      )
    )
  );

  createNewPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createNewPortfolio),
      mergeMap(action =>
        this.restApiService.create<RestPortfolioModel>(`Portfolio`, action.portfolioDetails).pipe(
          tap(portfolioDetails => {
            this.router.navigate([AppRoutes.SETTINGS_PORTFOLIO, portfolioDetails.id]).then();
          }),
          mergeMap(portfolioDetails => [
            loadPortfolioDetailsSuccess({
              portfolioDetails,
            }),
            loadAllUserPortfolios(),
          ]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.WhileCreatingNewPortfolio);
            return of(updatePortfolioDetailsFailed());
          })
        )
      )
    )
  );

  loadPropertyManagers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPropertyManagers),
      exhaustMap(action =>
        this.propertiesService.getPropertyManagers().pipe(
          map(propertyManagers => loadPropertyManagersSucceeded({ propertyManagers })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingPropertyManagers);
            return of(loadPropertyManagersFailed());
          })
        )
      )
    )
  );

  loadPropertyManagersForProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPropertyManagersForProperty),
      switchMap(action =>
        this.propertiesService.getPropertyManagersForProperty(action.propertyId).pipe(
          map(managerList => loadPropertyManagersForPropertySuccess({ propertyManagers: managerList || [] })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingPropertyManagersForProperty);
            return of(loadPropertyManagersForPropertyFailed());
          })
        )
      )
    )
  );

  updateProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProperty),
      mergeMap(action =>
        this.propertiesService.update(action.propertyDetails).pipe(
          map(propertyDetails => loadPropertyDetailsSuccess({ propertyDetails })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingPropertyDetails);
            return of(updatePropertyFailed());
          })
        )
      )
    )
  );

  deletePortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deletePortfolio),
      mergeMap(action =>
        this.restApiService.delete<RestPortfolioModel>(`Portfolio/${action.portfolioId}`).pipe(
          map(() => {
            this.snackbarService.success(`${action.portfolioName} deleted`);
            return loadAllUserPortfolios();
          }),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.RemovingPortfolio);
            return of(updatePortfolioDetailsFailed());
          })
        )
      )
    )
  );

  removePropertyManagers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removePropertyManagers),
      mergeMap(action =>
        this.propertiesService.removePropertyManagers(action.propertyManagerModels).pipe(
          map(() => removePropertyManagersSuccess()),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.RemovingManagerFromProperty);
            return of(removePropertyManagersFailed());
          })
        )
      )
    )
  );

  setPropertyManagers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPropertyManagers),
      withLatestFrom(this.store.select(selectPropertyDetails)),
      mergeMap(([action, property]) =>
        this.propertiesService.setPropertyManagers(action.propertyManagerModels).pipe(
          mergeMap(() => [setPropertyManagersSuccess(), loadPropertyDetails({ propertyId: property.id })]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.AssigningManagerToProperty);
            return of(setPropertyManagersFailed());
          })
        )
      )
    )
  );

  changePropertyManagers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePropertyManagers),
      withLatestFrom(this.store.select(selectPropertyDetails)),
      mergeMap(([action, property]) =>
        this.propertiesService
          .removePropertyManagers(action.replacePropertyManagers.oldManager)
          .pipe(map(() => setPropertyManagers({ propertyManagerModels: action.replacePropertyManagers.newManager })))
      )
    )
  );

  getPropertyUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPropertyUnits),
      switchMap(action =>
        this.unitsService.getPropertyUnits(action.propertyId).pipe(
          map(propertyUnits => getPropertyUnitsSuccess({ propertyUnits })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingPropertyUnits);
            return of(getPropertyUnitsFailed());
          })
        )
      )
    )
  );

  getUnitAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUnitAttachments),
      switchMap(action =>
        this.unitsService.getUnitAttachments(action.unitId).pipe(
          map(attachments => getUnitAttachmentsSuccess({ unitAttachments: attachments || [] })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingUnitsAttachments);
            return of(getPropertyUnitsFailed());
          })
        )
      )
    )
  );

  setUnitAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setUnitAttachment),
      mergeMap(action =>
        this.unitsService.setAttachment(action.attachmentToEntity).pipe(
          mergeMap(() => [
            setUnitAttachmentSuccess(),
            getUnitAttachments({ unitId: action.attachmentToEntity.unitId }),
          ]),
          tap(() => {
            const attachmentType = action.attachmentToEntity?.attachmentType as UnitAttachmentType;
            let message = 'Attachment(s) uploaded';
            switch (attachmentType) {
              case UnitAttachmentType.Photo:
                {
                  message = 'Photo(s) uploaded';
                }
                break;
              case UnitAttachmentType.FloorPlan:
                {
                  message = 'Floorplan uploaded';
                }
                break;
            }
          }),
          catchError(err => {
            const attachmentType = action.attachmentToEntity?.attachmentType as UnitAttachmentType;
            let message = SnackbarErrorMessage.AddingAttachment;
            switch (attachmentType) {
              case UnitAttachmentType.Photo:
                {
                  message = SnackbarErrorMessage.AddingPhoto;
                }
                break;
              case UnitAttachmentType.FloorPlan:
                {
                  message = SnackbarErrorMessage.AddingFloorplan;
                }
                break;
            }

            this.snackbarService.error(message);
            return of(setUnitAttachmentFail());
          })
        )
      )
    )
  );

  deleteUnitAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteUnitAttachment),
      mergeMap(action =>
        this.unitsService.deleteUnitAttachment(action.unitId, action.attachment.id).pipe(
          switchMap(() =>
            iif(
              () => !!action.attachment?.fileUploadId,
              of(action.attachment?.fileUploadId).pipe(map((fileId: number) => deleteFileFromRepository({ fileId }))),
              of(null)
            )
          ),
          mergeMap(() => [deleteUnitAttachmentSuccess(), getUnitAttachments({ unitId: action.unitId })]),
          tap(() => {
            const attachmentType = action.attachment?.targetAttachmentType as UnitAttachmentType;
            let message = 'Attachment removed';
            switch (attachmentType) {
              case UnitAttachmentType.Photo:
                {
                  message = 'Photo deleted';
                }
                break;
              case UnitAttachmentType.FloorPlan:
                {
                  message = 'Floorplan deleted';
                }
                break;
            }
          }),
          catchError(() => {
            const attachmentType = action.attachment?.targetAttachmentType as UnitAttachmentType;
            let message = SnackbarErrorMessage.DeletingAttachment;
            switch (attachmentType) {
              case UnitAttachmentType.Photo:
                {
                  message = SnackbarErrorMessage.DeletingPhoto;
                }
                break;
              case UnitAttachmentType.FloorPlan:
                {
                  message = SnackbarErrorMessage.DeletingFloorplan;
                }
                break;
            }

            this.snackbarService.error(message);
            return of(deleteUnitAttachmentFailed());
          })
        )
      )
    )
  );

  deleteFileFromRepository$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFileFromRepository),
      mergeMap(action =>
        this.fileUploadsService.removeFile(action.fileId).pipe(
          map(() => deleteFileFromRepositorySuccess()),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingFile);
            return of(deleteFileFromRepositoryFailed());
          })
        )
      )
    )
  );

  loadPropertyCollections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPropertyCollections),
      switchMap(() =>
        this.restApiService.getData<RestPropertyCollectionModel[]>(`PropertyCollections/all`).pipe(
          map(result => loadPropertyCollectionsSuccess({ items: result || [] })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorLoadingPropertyCollections);
            return of(loadPropertyCollectionsFailed());
          })
        )
      )
    )
  );

  loadAllUserCollections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllUserCollections),
      switchMap(action =>
        this.restApiService.getData<RestPropertyCollectionModel[]>(`PropertyCollections/all`).pipe(
          map(result => loadAllUserCollectionsSuccess({ collectionItems: result || [] })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorLoadingPropertyCollections);
            return of(loadAllUserCollectionsFailed());
          })
        )
      )
    )
  );

  addPropertyCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addPropertyCollection),
      switchMap(({ data }) =>
        this.restApiService.create<CreatePropertyCollectionReqI & { id: number }>(`PropertyCollections`, data).pipe(
          tap(res => {
            this.router.navigate([AppRoutes.SETTINGS_COLLECTION, res.id]).then();
          }),
          mergeMap(res => [addPropertyCollectionSuccess({ collection: res }), loadAllUserCollections()]),
          catchError(err => {
            const errorMsg = err?.error?.ErrorCode
              ? err.error.ErrorCode
              : SnackbarErrorMessage.ErrorAddingPropertyCollections;
            this.snackbarService.error(errorMsg);
            return of(addPropertyCollectionError());
          })
        )
      )
    )
  );

  savePropertyCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePropertyCollection),
      switchMap(action =>
        this.restApiService
          .update<UpdatePropertyCollectionReqI>(`PropertyCollections/${action.id}`, {
            name: action.name,
          })
          .pipe(
            tap(() => {
              this.snackbarService.success(`Collection name changed!`);
            }),
            mergeMap(res => [savePropertyCollectionSuccess({ collection: res }), loadAllUserCollections()]),
            catchError(err => {
              const errorMsg = err?.error?.ErrorCode
                ? err.error.ErrorCode
                : SnackbarErrorMessage.ErrorUpdatingPropertyCollections;
              this.snackbarService.error(errorMsg);
              return of(savePropertyCollectionError());
            })
          )
      )
    )
  );

  deletePropertyCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deletePropertyCollection),
      mergeMap(action =>
        this.restApiService.delete<RestPortfolioModel>(`PropertyCollections/${action.id}`).pipe(
          tap(() => {
            this.snackbarService.success(`${action.collectionName} deleted`);
          }),
          mergeMap(() => [deletePropertyCollectionSuccess({ id: action.id }), loadAllUserCollections()]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorRemovingPropertyCollections);
            return of(deletePropertyCollectionError());
          })
        )
      )
    )
  );

  addPropertiesToCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addPropertiesToCollection),
      switchMap(({ data }) =>
        this.restApiService
          .create<RestPropertyCollectionModel>(`PropertyCollections/${data.collectionId}/properties`, {
            propertyIds: data.propertyIds,
          })
          .pipe(
            map(res => addPropertiesToCollectionSuccess({ collection: res })),
            catchError(err => {
              const errorMsg = err?.error?.ErrorCode
                ? err.error.ErrorCode
                : SnackbarErrorMessage.ErrorAddingPropertyToCollections;
              this.snackbarService.error(errorMsg);
              return of(addPropertiesToCollectionError());
            })
          )
      )
    )
  );

  removePropertiesFromCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removePropertiesFromCollection),
      switchMap(({ data }) =>
        this.restApiService
          .delete<RestPropertyCollectionModel>(`PropertyCollections/${data.collectionId}/properties`, {
            propertyIds: data.propertyIds,
          })
          .pipe(
            map(res => removePropertiesFromCollectionSuccess({ collection: res })),
            catchError(err => {
              const errorMsg = err?.error?.ErrorCode
                ? err.error.ErrorCode
                : SnackbarErrorMessage.ErrorRemovingPropertyFromCollection;
              this.snackbarService.error(errorMsg);
              return of(removePropertiesFromCollectionError());
            })
          )
      )
    )
  );

  addUsersToCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addUsersToCollection),
      switchMap(({ data }) =>
        this.restApiService
          .create<RestPropertyCollectionModel>(`PropertyCollections/${data.collectionId}/users`, {
            userIds: data.userIds,
          })
          .pipe(
            map(res => addUsersToCollectionSuccess({ collection: res })),
            catchError(err => {
              const errorMsg = err?.error?.ErrorCode
                ? err.error.ErrorCode
                : SnackbarErrorMessage.ErrorAddingUserToCollections;
              this.snackbarService.error(errorMsg);
              return of(addUsersToCollectionError());
            })
          )
      )
    )
  );

  removeUsersFromCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeUsersFromCollection),
      switchMap(({ data }) =>
        this.restApiService
          .delete<RestPropertyCollectionModel>(`PropertyCollections/${data.collectionId}/users`, {
            userIds: data.userIds,
          })
          .pipe(
            map(res => removeUsersFromCollectionSuccess({ collection: res })),
            catchError(err => {
              const errorMsg = err?.error?.ErrorCode
                ? err.error.ErrorCode
                : SnackbarErrorMessage.ErrorRemovingUsersFromCollection;
              this.snackbarService.error(errorMsg);
              return of(removeUsersFromCollectionError());
            })
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private restApiService: RestapiService,
    private snackbarService: SnackbarService,
    private propertiesService: PropertiesService,
    private store: Store<{}>,
    private router: Router,
    private unitsService: UnitsService,
    private fileUploadsService: FileUploadsService,
    private portfolioService: PortfolioService
  ) {}
}
